import { TUser } from "@/types/types";

type ProfileField = {
  id: string;
  label: string;
  check: (data: TUser | undefined) => boolean;
};

type ProfileData = TUser & Record<string, unknown>;

export interface ProfileStep {
  id: number;
  label: string;
  fields: ProfileField[];
  weight: number;
}

const isNonEmpty = (value: unknown) => {
  if (typeof value === "string") return value.trim().length > 0;
  if (typeof value === "number") return value > 0;
  if (typeof value === "boolean") return true;
  return value !== undefined && value !== null;
};

const hasArrayValue = (value: unknown) =>
  Array.isArray(value) && value.length > 0;

const hasObjectValue = (value: unknown): boolean => {
  if (!value || typeof value !== "object" || Array.isArray(value)) return false;

  return Object.values(value).some((item) => {
    if (Array.isArray(item)) return item.length > 0;
    if (item && typeof item === "object") return hasObjectValue(item);
    return isNonEmpty(item);
  });
};

const hasAnyValue = (data: TUser | undefined, keys: string[]) => {
  if (!data) return false;

  const profileData = data as ProfileData;
  return keys.some((key) => {
    const value = profileData[key];

    if (Array.isArray(value)) return hasArrayValue(value);
    if (value && typeof value === "object") return hasObjectValue(value);

    return isNonEmpty(value);
  });
};

const hasAddressValue = (
  data: TUser | undefined,
  addressKey: keyof NonNullable<TUser["address"]>,
) => {
  if (!data) return false;

  return isNonEmpty(data.address?.[addressKey]);
};

const hasProfileLinks = (data: TUser | undefined) => {
  if (!data) return false;

  const links = (data as ProfileData).profileLinks;
  if (!Array.isArray(links)) return false;

  return links.some((link) => {
    if (!link || typeof link !== "object") return false;
    return isNonEmpty((link as Record<string, unknown>).url);
  });
};

const hasLanguages = (data: TUser | undefined) =>
  hasArrayValue(data?.languagesWithFluency) || hasAnyValue(data, ["language"]);

export const profileSteps: ProfileStep[] = [
  {
    id: 1,
    label: "Personal Details",
    fields: [
      {
        id: "fullName",
        label: "Full name",
        check: (data) => hasAnyValue(data, ["fullName"]),
      },
      { id: "age", label: "Age", check: (data) => hasAnyValue(data, ["age"]) },
      {
        id: "gender",
        label: "Gender",
        check: (data) => hasAnyValue(data, ["gender"]),
      },
      {
        id: "bodyType",
        label: "Body type",
        check: (data) => hasAnyValue(data, ["bodyType"]),
      },
      {
        id: "skinTone",
        label: "Skin tone",
        check: (data) => hasAnyValue(data, ["skinTone"]),
      },
      {
        id: "phoneNumber",
        label: "Phone number",
        check: (data) => hasAnyValue(data, ["phoneNumber"]),
      },
      {
        id: "country",
        label: "Country",
        check: (data) =>
          hasAnyValue(data, ["country"]) || hasAddressValue(data, "country"),
      },
      {
        id: "state",
        label: "State",
        check: (data) =>
          hasAnyValue(data, ["state"]) || hasAddressValue(data, "state"),
      },
      {
        id: "city",
        label: "City",
        check: (data) =>
          hasAnyValue(data, ["city"]) || hasAddressValue(data, "city"),
      },
      {
        id: "pincode",
        label: "Pincode",
        check: (data) =>
          hasAnyValue(data, ["pincode"]) || hasAddressValue(data, "zipCode"),
      },
    ],
    weight: 40,
  },
  {
    id: 2,
    label: "Professional Details",
    fields: [
      {
        id: "skills",
        label: "Available skills",
        check: (data) => hasAnyValue(data, ["topics", "skills", "skillsMulti"]),
      },
      { id: "profileLinks", label: "Profile links", check: hasProfileLinks },
      {
        id: "portfolioVideos",
        label: "Portfolio videos",
        check: (data) => hasAnyValue(data, ["portfolioVideos", "videos"]),
      },
      { id: "languages", label: "Languages", check: hasLanguages },
      {
        id: "availableHoursPerWeek",
        label: "Weekly availability",
        check: (data) =>
          hasAnyValue(data, ["availableHoursPerWeek", "hoursPerWeek"]),
      },
    ],
    weight: 35,
  },
  {
    id: 3,
    label: "Outfits & Backgrounds",
    fields: [
      {
        id: "wardrobeImages",
        label: "Wardrobe styles",
        check: (data) =>
          hasAnyValue(data, ["wardrobeImages", "wardrobeStyles"]),
      },
      {
        id: "backgroundImages",
        label: "Background styles",
        check: (data) =>
          hasAnyValue(data, ["backgroundImages", "availableBackgrounds"]),
      },
    ],
    weight: 25,
  },
];

export const calculateStepCompletion = (
  data: TUser | undefined,
  step: ProfileStep,
) => {
  const fieldStatuses = step.fields.map((field) => ({
    ...field,
    completed: field.check(data),
  }));
  const completedFields = fieldStatuses.filter((field) => field.completed);
  const missingFields = fieldStatuses.filter((field) => !field.completed);

  return {
    completedCount: completedFields.length,
    totalCount: step.fields.length,
    percentage: Math.round((completedFields.length / step.fields.length) * 100),
    weight: step.weight,
    completedFields,
    missingFields,
  };
};

export const useProfileCompletion = (data: TUser | undefined) => {
  const stepCompletions = profileSteps.map((step) =>
    calculateStepCompletion(data, step),
  );

  const totalProfileCompletion = stepCompletions.reduce(
    (total, step) => total + (step.percentage * step.weight) / 100,
    0,
  );
  const roundedProfileCompletion = Math.round(totalProfileCompletion);

  return {
    totalProfileCompletion: roundedProfileCompletion,
    stepCompletions,
    isProfileComplete: roundedProfileCompletion === 100,
  };
};
